import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    OmFormDropdownFieldWrapper,
    OmFormInputFieldWrapper,
    OmFormError,
    OmRadioChipButtonGroup,
} from '@om/component-library-react';

import { getName } from '@om/redux/selectors/raOptimal/captureDetails';
import {
    setSourceOfFundsValue,
    setEmploymentPositionValue,
    setEmploymentPositionPipValue,
    setProminentPersonValue,
    setEmploymentIndustryValue,
    setEmploymentIndustryOtherValue,
} from '@om/redux/actions/raOptimal/paymentDetails';
import {
    getEmploymentIndustryRequired,
    getEmploymentIndustryOtherRequired,
    getSourceOfFunds,
    getEmploymentPosition,
    getEmploymentPositionPip,
    getProminentPerson,
    getEmploymentIndustry,
    getEmploymentIndustryOther,
} from '@om/redux/selectors/raOptimal/paymentDetails';
import {
    getReferenceEmploymentPositions,
    getReferenceEmploymentPositionsPip,
    getReferenceEmploymentIndustries,
    getReferenceSourceOfFunds,
} from '@om/redux/selectors/raOptimal/referenceData';

const LegalDetailsSection = () => {
    const dispatch = useDispatch();
    const {
        employmentIndustryRequired,
        employmentIndustryOtherRequired,
        name,
        sourceOfFunds,
        employmentPosition,
        employmentPositionPip,
        prominentPerson,
        employmentIndustry,
        employmentIndustryOther,
        referenceEmploymentPositions,
        referenceEmploymentPositionsPip,
        referenceEmploymentIndustries,
        referenceSourceOfFunds,
    } = useSelector(
        createStructuredSelector({
            employmentIndustryRequired: getEmploymentIndustryRequired,
            employmentIndustryOtherRequired: getEmploymentIndustryOtherRequired,
            name: getName,
            sourceOfFunds: getSourceOfFunds,
            employmentPosition: getEmploymentPosition,
            employmentPositionPip: getEmploymentPositionPip,
            prominentPerson: getProminentPerson,
            employmentIndustry: getEmploymentIndustry,
            employmentIndustryOther: getEmploymentIndustryOther,
            referenceEmploymentPositions: getReferenceEmploymentPositions,
            referenceEmploymentPositionsPip: getReferenceEmploymentPositionsPip,
            referenceEmploymentIndustries: getReferenceEmploymentIndustries,
            referenceSourceOfFunds: getReferenceSourceOfFunds,
        })
    );
    const radioChipButtonListItems = [
        { value: true, description: 'Yes' },
        { value: false, description: 'No' },
    ];

    return (
        <div className="legal-section" id="legal-anchor">
            <h4 className="heading">Source of funds</h4>
            <p className="subheading">
                {name.value}, to protect you and fellow South Africans from fraud and money laundering, we need to know
                about your earnings.
            </p>
            <section className="form">
                <OmFormDropdownFieldWrapper
                    useNativeMobileDropdown
                    id="sourceOfFunds"
                    required
                    form-id="paymentDetailsForm"
                    placeholder="Source of funds"
                    name="sourceOfFunds"
                    align="left"
                    value={sourceOfFunds.value}
                    state={sourceOfFunds.status}
                    errorMessage={sourceOfFunds.error}
                    options={
                        referenceSourceOfFunds &&
                        JSON.stringify(
                            referenceSourceOfFunds.map((option) => ({ value: option.code, label: option.description }))
                        )
                    }
                    onOnSelect={(event) => dispatch(setSourceOfFundsValue(event.detail.id))}
                />
                <OmFormDropdownFieldWrapper
                    useNativeMobileDropdown
                    id="employmentPosition"
                    required
                    form-id="paymentDetailsForm"
                    placeholder="What do you do for a living?"
                    name="employmentPosition"
                    align="left"
                    value={employmentPosition.value}
                    state={employmentPosition.status}
                    errorMessage={employmentPosition.error}
                    options={
                        referenceEmploymentPositions &&
                        JSON.stringify(
                            referenceEmploymentPositions.map((option) => ({
                                value: option.code,
                                label: option.description,
                            }))
                        )
                    }
                    onOnSelect={(event) => dispatch(setEmploymentPositionValue(event.detail.id))}
                />
                {employmentIndustryRequired && (
                    <OmFormDropdownFieldWrapper
                        useNativeMobileDropdown
                        v-show="employmentIndustryRequired"
                        id="employmentIndustry"
                        required="true"
                        form-id="paymentDetailsForm"
                        placeholder="What industry do you work in?"
                        name="employmentIndustry"
                        align="left"
                        value={employmentIndustry.value}
                        state={employmentIndustry.status}
                        errorMessage={employmentIndustry.error}
                        options={
                            referenceEmploymentIndustries &&
                            JSON.stringify(
                                referenceEmploymentIndustries.map((option) => ({
                                    value: option.code,
                                    label: option.description,
                                }))
                            )
                        }
                        onOnSelect={(event) => dispatch(setEmploymentIndustryValue(event.detail.id))}
                    />
                )}
                {employmentIndustryOtherRequired && (
                    <OmFormInputFieldWrapper
                        id="employmentIndustryOther"
                        placeholder="Please specify"
                        required
                        size="full"
                        form-id="paymentDetailsForm"
                        name="employmentIndustryOther"
                        align="left"
                        validationDelay={DEFAULT_VALIDATION_DELAY}
                        value={employmentIndustryOther.value}
                        state={employmentIndustryOther.status}
                        errorMessage={employmentIndustryOther.error}
                        onPersist={(event) => dispatch(setEmploymentIndustryOtherValue(event.detail))}
                    />
                )}
                <p className="form-text">Are you a prominent person?</p>
                <OmRadioChipButtonGroup
                    id="prominentPerson"
                    value={prominentPerson.value}
                    groupName="prominentPersonGroup"
                    onOnSelection={(event) => dispatch(setProminentPersonValue(event.detail === 'true'))}
                >
                    <ul slot="list-items">
                        {radioChipButtonListItems.map((listItem, index) => {
                            return (
                                <li value={listItem.value} key={index}>
                                    {listItem.description}
                                </li>
                            );
                        })}
                    </ul>
                </OmRadioChipButtonGroup>
                <OmFormError message={prominentPerson.error}></OmFormError>

                {prominentPerson.value === true && (
                    <div>
                        <OmFormDropdownFieldWrapper
                            useNativeMobileDropdown
                            id="employmentPositionPip"
                            required="true"
                            form-id="employmentPositionPip"
                            placeholder="What is your role?"
                            name="employmentPositionPip"
                            align="left"
                            value={employmentPositionPip.value}
                            state={employmentPositionPip.status}
                            errorMessage={employmentPositionPip.error}
                            options={
                                referenceEmploymentPositionsPip &&
                                JSON.stringify(
                                    referenceEmploymentPositionsPip.map((option) => ({
                                        value: option.code,
                                        label: option.description,
                                    }))
                                )
                            }
                            onOnSelect={(event) => dispatch(setEmploymentPositionPipValue(event.detail.id))}
                        />
                    </div>
                )}
            </section>
        </div>
    );
};

export default LegalDetailsSection;
